import React, { useState } from "react";
import { Button, ListGroup, Spinner, Alert, Card, Collapse, Modal, Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faClock,
  faMagnifyingGlassChart,
  faTrashCan,
  faFileCode,
  faDownload,
  faPlay,
  faCheckCircle
} from "@fortawesome/free-solid-svg-icons";
import { Colors } from "../utils/style";
import { useStore } from "../store";
import axios from "axios";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { coy } from "react-syntax-highlighter/dist/esm/styles/prism";
import { useNavigate } from "react-router-dom";

type PropTypes = {
  title: string;
  icon?: IconProp;
  isExpanded: boolean;
  setExpandedFile: (file: string | null) => void;
  setSelectedFileForVisualization: (file: string) => void;
};

function DataSourceCardComponent(props: PropTypes) {
  const {
    userFilesNames,
    setUserFilesNames,
    setSelectedFileName,
    setSelectedHistoryKey,
    setSelectedHistoryData,
    setCode,
    setVisualisationImage,
    setVisualisation,

    setInstructions,
    setIinterpretations,
    setVisualisationType,
    setExplanations,
    setCurrentHistoryIndex
  } = useStore();

  const [historyKeys, setHistoryKeys] = useState<string[]>([]);
  const [selectedHistory, setSelectedHistory] = useState<any>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [activeHistoryItem, setActiveHistoryItem] = useState<string | null>(null);

  const navigate = useNavigate();

  const getAuthToken = (): string => localStorage.getItem("tokenLida") || "";

  const fetchHistoryKeys = async (): Promise<void> => {
    if (props.isExpanded) {
      props.setExpandedFile(null);
      return;
    }

    props.setExpandedFile(props.title);
    setLoadingHistory(true);
    setErrorMessage(null);
    setHistoryKeys([]);
    setSelectedHistory(null);

    try {
      const token = getAuthToken();
      if (!token) throw new Error("You must log in first!");

      const baseUrl = process.env.REACT_APP_API_HOST?.replace(/\/$/, "");
      const apiUrl = `${baseUrl}/history/${props.title}`;

      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (Array.isArray(response.data) && response.data.length > 0) {
        setHistoryKeys(response.data);
      }
    } catch (error: any) {
      setErrorMessage(error.response?.data?.detail || "Failed to fetch history.");
    }
    setLoadingHistory(false);
  };

  const fetchHistoryDetails = async (key: string): Promise<any> => {
    setLoadingDetails(true);
    setErrorMessage(null);
    setActiveHistoryItem(key);

    try {
      const token = getAuthToken();
      if (!token) throw new Error("You must log in first!");

      const baseUrl = process.env.REACT_APP_API_HOST?.replace(/\/$/, "");
      const apiUrl = `${baseUrl}/history/${props.title}/${encodeURIComponent(key)}`;

      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const historyData = response.data;
      // Store the key with the history data for easier reference
      historyData.key = key;
      setSelectedHistory(historyData);
      return historyData;
    } catch (error: any) {
      setErrorMessage(error.response?.data?.detail || "Failed to fetch history details.");
      return null;
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    setShowConfirmModal(false);

    try {
      const token = getAuthToken();
      if (!token) throw new Error("You must log in first!");

      const baseUrl = process.env.REACT_APP_API_HOST?.replace(/\/$/, "");
      const apiUrl = `${baseUrl}/delete_user_file/${props.title}`;

      await axios.delete(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (userFilesNames) {
        const updatedFiles = userFilesNames.filter((file) => file !== props.title);
        setUserFilesNames(updatedFiles);
      }
    } catch (error: any) {
      setErrorMessage(error.response?.data?.detail || "Failed to delete file.");
    }
  };

  // Basic navigation to visualization (without history)
  const navigateToVisualization = () => {
    setSelectedFileName(props.title);

    setLoadingDetails(true);

    const token = getAuthToken();
    if (!token) {
      setErrorMessage("You must log in first!");
      setLoadingDetails(false);
      navigate("/");
      return;
    }

    const baseUrl = process.env.REACT_APP_API_HOST?.replace(/\/$/, "");


    axios.get(
      `${baseUrl}/summarize_user_file/${props.title}`,
      {
        headers: {
          "Content-Type": "application/json",
          "Referrer-Policy": "no-referrer",
          Authorization: `Bearer ${token}`,
        },
      }
    )
    .then((response) => {
      const { setSummary } = useStore.getState();
      setSummary(response.data.summary);

      const body = {
        filename: props.title,
        n: 5,
        textgen_config: {
          temperature: 0,
          n: 1,
          model: "gpt-4o",
          max_tokens: null,
          provider: "openai",
        },
      };

      return axios.post(
        `${baseUrl}/user_goal`,
        body,
        {
          headers: {
            "Content-Type": "application/json",
            "Referrer-Policy": "no-referrer",
            Authorization: `Bearer ${token}`,
          },
        }
      );
    })
    .then((response) => {
      if (response) {
        const { setGoals } = useStore.getState();
        setGoals(response.data.data);
      }
    })
    .catch((error) => {
      console.error("Error loading data:", error);
      setErrorMessage("Failed to load file data");
    })
    .finally(() => {
      setLoadingDetails(false);
      navigate("/");
    });
  };
  // Enhanced function to navigate with history
  const navigateToVisualizationWithHistory = async (key: string, historyData?: any, refineIndex: number = -1) => {
    // If historyData is not provided, fetch it first

    let historyToUse = historyData;
    if (!historyToUse) {
      historyToUse = await fetchHistoryDetails(key);
      if (!historyToUse) return;
    }
    if (refineIndex==-1) {
        refineIndex = historyToUse.history.length-1;
        }
    setCurrentHistoryIndex(refineIndex)
    navigateToVisualization();
    setSelectedFileName(props.title);
    setSelectedHistoryKey(key);
    setSelectedHistoryData(historyToUse);
    setInstructions(historyToUse.goal.question);
    setVisualisationType(historyToUse.goal.visualization);
    setCode(historyToUse.history[refineIndex].charts.code);
    setVisualisationImage(historyToUse.history[refineIndex].charts.raster || "");
    setVisualisation(historyToUse.history[refineIndex].charts.svg_plot);
    setIinterpretations(historyToUse.history[refineIndex].interpretation);
    setExplanations(historyToUse.history[refineIndex].explanations);


  };

  const exportSVG = (svgContent: string) => {
    const blob = new Blob([svgContent], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    downloadFile(url, `${props.title}_visualization.svg`);
  };

  const exportImage = (svgContent: string, format: string) => {
    const img = new Image();
    const svg = new Blob([svgContent], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svg);

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.drawImage(img, 0, 0);
        const imageType = format === 'png' ? 'image/png' : 'image/jpeg';
        const dataUrl = canvas.toDataURL(imageType);

        const link = document.createElement('a');
        link.download = `${props.title}_visualization.${format}`;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      URL.revokeObjectURL(url);
    };

    img.src = url;
  };

  const downloadFile = (url: string, filename: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-100">
      <Card
        className="shadow-sm mx-auto w-100"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          padding: "15px",
          marginBottom: "10px",
        }}
      >
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={props.icon || faFileCode} color={Colors.DARK_BLUE} size="lg" />
              <h6 className="mb-0" style={{ color: Colors.DARK_BLUE, fontWeight: "bold" }}>
                {props.title}
              </h6>
            </div>
          </div>

          <div className="d-flex justify-content-between mt-2">
            {/* History Icon */}
            <FontAwesomeIcon
              icon={faClock}
              color={"blue"}
              size="lg"
              style={{ cursor: "pointer" }}
              onClick={fetchHistoryKeys}
              title="View History"
            />

            <FontAwesomeIcon
              icon={faMagnifyingGlassChart}
              color={"green"}
              size="lg"
              style={{ cursor: "pointer" }}
              onClick={navigateToVisualization}
              title="Visualize (Fresh Start)"
            />

            <FontAwesomeIcon
              icon={faTrashCan}
              color={"red"}
              size="lg"
              style={{ cursor: "pointer" }}
              onClick={() => setShowConfirmModal(true)}
              title="Delete File"
            />
          </div>
        </Card.Body>

        <Collapse in={props.isExpanded}>
          <Card.Body style={{ overflowX: "auto", maxWidth: "100%" }}>
            <Card
              className="shadow-sm p-4 w-100 mx-auto"
              style={{
                borderRadius: "12px",
                backgroundColor: "#ffffff",
                padding: "20px",
              }}
            >
              <Card.Header
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  padding: "12px",
                }}
              >
                History for {props.title}
              </Card.Header>

              <Card.Body>
                {loadingHistory ? (
                  <Spinner animation="border" />
                ) : historyKeys.length > 0 ? (
                  <ListGroup style={{ width: "100%" }}>
                    {historyKeys.map((key) => (
                      <ListGroup.Item
                        key={key}
                        action
                        active={activeHistoryItem === key}
                        className="d-flex justify-content-between align-items-center"
                        onClick={() => fetchHistoryDetails(key)}
                        style={{ cursor: "pointer", borderRadius: "6px", padding: "14px" }}
                      >
                        <span>{key}</span>
                        <div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToVisualizationWithHistory(key);
                            }}
                          >
                            <FontAwesomeIcon icon={faPlay} className="me-1" /> Resume
                          </Button>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <Alert variant="info">No history available.</Alert>
                )}

                <Collapse in={!!selectedHistory}>
                  <div className="mt-3 p-3 border rounded" style={{ backgroundColor: "#ffffff" }}>
                    {loadingDetails ? (
                      <Spinner animation="border" />
                    ) : selectedHistory ? (
                      <>
                        <div className="mb-3 p-3 border rounded bg-light">
                          <h5 className="mb-3">Query:</h5>
                          <h6>{selectedHistory.goal.question}</h6>
                          <p>
                            <strong>Visualization:</strong> {selectedHistory.goal.visualization}
                          </p>

                          <div className="d-flex justify-content-end">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => navigateToVisualizationWithHistory(selectedHistory.key, selectedHistory)}
                            >
                              <FontAwesomeIcon icon={faPlay} className="me-1" /> Resume from here
                            </Button>
                          </div>
                        </div>



                        {selectedHistory.history?.length > 0 && (
                          <h5 className="mt-4 mb-3">History:</h5>
                        )}

                        {selectedHistory.history?.map(
                          (refine: { instruction: string; charts: { code: string; svg_plot?: string } }, index: number) => (
                            <div key={index} className="mt-3 p-3 border rounded">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="mb-0">{refine.instruction}</h6>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => navigateToVisualizationWithHistory(selectedHistory.key, selectedHistory, index)}
                                >
                                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Resume from here
                                </Button>
                              </div>

                              <div style={{ overflowX: "auto" }}>
                                <SyntaxHighlighter language="python" style={coy}>
                                  {refine.charts.code}
                                </SyntaxHighlighter>
                              </div>

                              {refine.charts.svg_plot ? (
                                <>
                                  <div className="d-flex justify-content-end mb-2">
                                    <div style={{ position: "relative", display: "inline-block" }}>
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => setIsExportOpen(!isExportOpen)}
                                      >
                                        <FontAwesomeIcon icon={faDownload} className="me-1" />
                                        Export
                                      </Button>
                                      {isExportOpen && (
                                        <div
                                          style={{
                                            position: "absolute",
                                            top: "100%",
                                            right: "0",
                                            backgroundColor: "white",
                                            border: "1px solid #ddd",
                                            borderRadius: "5px",
                                            boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
                                            zIndex: 1000,
                                            width: "150px",
                                          }}
                                        >
                                          <Button
                                            variant="light"
                                            size="sm"
                                            className="w-100 text-start"
                                            onClick={() => exportSVG(refine.charts.svg_plot as string)}
                                          >
                                            Export as SVG
                                          </Button>
                                          <Button
                                            variant="light"
                                            size="sm"
                                            className="w-100 text-start"
                                            onClick={() => exportImage(refine.charts.svg_plot as string, "png")}
                                          >
                                            Export as PNG
                                          </Button>
                                          <Button
                                            variant="light"
                                            size="sm"
                                            className="w-100 text-start"
                                            onClick={() => exportImage(refine.charts.svg_plot as string, "jpeg")}
                                          >
                                            Export as JPEG
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div style={{ maxWidth: "100%", overflowX: "auto" }}>
                                    <div dangerouslySetInnerHTML={{ __html: refine.charts.svg_plot }} />
                                  </div>
                                </>
                              ) : (
                                <Alert variant="info">No refined visualization available.</Alert>
                              )}
                            {selectedHistory.history[index].interpretation?.content && (
                                <Alert variant="light" style={{ marginTop: "15px", fontStyle: "italic" }}>
                                    <strong>Interpretation:</strong> {selectedHistory.history[index].interpretation?.content}
                                </Alert>
                            )}
                            </div>
                          )
                        )}


                      </>
                    ) : null}
                  </div>
                </Collapse>
              </Card.Body>
            </Card>
          </Card.Body>
        </Collapse>

        <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Deletion</Modal.Title>
          </Modal.Header>
          <Modal.Body>Are you sure you want to delete this file?</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      </Card>
    </div>
  );
}

export default DataSourceCardComponent;